/**
 * useDefectsConfig.js
 *
 * Hook for managing vessel-specific defect configuration settings.
 * WBC3 Edition: 4 settings for defect management
 *
 * Settings:
 * 1. Default Risk Level - Medium (default), Low, High, Critical
 * 2. Resolution Target Days - Default 30, range 1-365
 * 3. Require Corrective Action on Close - Default ON (WBC3 4.11.5 requirement)
 * 4. Show Resolved Defects - Toggle visibility of closed defects
 *
 * ANCHOR MODE compliant - Domain Spine v2
 */

import { useCallback, useMemo } from 'react';
import { useStorageState } from '../../useStorageState';
import { useVessel } from '../../../contexts/VesselContext';
import { usePermissions } from '../../usePermissions';
// import { useVesselCollections } from '../../useVesselCollections'; // Phase 2.6: Disabled for IndexedDB migration
import { logCRUDOperation, AUDIT_ACTIONS } from '../../../services/auditTrail';

// RBAC helper (same pattern as useCrewConfig)
const canDo = (can, resource, action) => {
  try {
    if (!can || typeof can !== 'function') return false;
    return (
      (can.length >= 2 && can(resource, action)) ||
      can(`${resource}:${action}`) ||
      can({ resource, action }) ||
      can(action, resource)
    );
  } catch {
    return false;
  }
};

/**
 * Risk level definitions
 */
export const RISK_LEVELS = {
  Critical: {
    id: 'Critical',
    label: 'Critical',
    description: 'Immediate action required - safety risk',
    color: 'red'
  },
  High: {
    id: 'High',
    label: 'High',
    description: 'Urgent attention needed within 24 hours',
    color: 'orange'
  },
  Medium: {
    id: 'Medium',
    label: 'Medium',
    description: 'Plan repair soon - within 7 days',
    color: 'yellow'
  },
  Low: {
    id: 'Low',
    label: 'Low',
    description: 'Can wait for scheduled maintenance',
    color: 'blue'
  }
};

/**
 * Default defects configuration
 */
const getDefaultConfig = () => ({
  version: 1,
  revision: 1,

  // Setting 1: Default risk level for new defects
  defaultRiskLevel: 'Medium',

  // Setting 2: Target days to resolve defects
  resolutionTargetDays: 30,

  // Setting 3: Require corrective action when closing (WBC3 4.11.5)
  // MUST be ON by default for regulatory compliance
  requireCorrectiveActionOnClose: true,

  // Setting 4: Show resolved/closed defects in the list
  showResolvedDefects: false,

  // Metadata
  lastModified: new Date().toISOString(),
  modifiedBy: { name: 'System', userId: 'system' }
});

/**
 * Hook for managing defects configuration
 */
export const useDefectsConfig = () => {
  const { getCurrentVessel } = useVessel();
  const currentVessel = getCurrentVessel();
  const { can, currentUser } = usePermissions();
  // const { collections } = useVesselCollections(); // Phase 2.6: Disabled for IndexedDB migration

  // Vessel-scoped storage
  const storageKey = 'defects_config';
  const { value: rawConfig, setValue: setConfig } = useStorageState(
    storageKey,
    getDefaultConfig()
  );

  // Ensure config has all required fields (handle upgrades)
  const config = useMemo(() => {
    const defaults = getDefaultConfig();
    return {
      ...defaults,
      ...rawConfig
    };
  }, [rawConfig]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  /**
   * Get current default risk level with full details
   */
  const currentDefaultRiskLevel = useMemo(() => {
    return RISK_LEVELS[config.defaultRiskLevel] || RISK_LEVELS.Medium;
  }, [config.defaultRiskLevel]);

  /**
   * Get resolution target
   */
  const resolutionTarget = useMemo(() => ({
    days: config.resolutionTargetDays,
    isDefault: config.resolutionTargetDays === 30
  }), [config.resolutionTargetDays]);

  // ============================================================================
  // MUTATIONS WITH AUDIT TRAIL
  // ============================================================================

  /**
   * Update a single setting
   */
  const updateSetting = useCallback(async (settingKey, value) => {
    if (!canDo(can, 'settings', 'update')) {
      console.warn('[useDefectsConfig] Permission denied');
      return false;
    }

    const oldValue = config[settingKey];

    const updatedConfig = {
      ...config,
      revision: (config.revision || 0) + 1,
      [settingKey]: value,
      lastModified: new Date().toISOString(),
      modifiedBy: { name: currentUser?.name, userId: currentUser?.id }
    };

    await setConfig(updatedConfig);

    // Log to audit trail
    // Phase 2.6: Audit temporarily disabled for IndexedDB migration
    // try {
    //   await logCRUDOperation(
    //     collections,
    //     AUDIT_ACTIONS.UPDATE,
    //     'defects_config',
    //     {
    //       vesselId: currentVessel?.id,
    //       setting: settingKey,
    //       newValue: value,
    //       oldValue
    //     },
    //     { userId: currentUser?.id, name: currentUser?.name }
    //   );
    // } catch (e) {
    //   console.warn('[useDefectsConfig] Audit log failed:', e);
    // }

    return true;
  }, [can, config, setConfig, currentUser, currentVessel]);

  /**
   * Update default risk level
   */
  const setDefaultRiskLevel = useCallback(async (level) => {
    if (!RISK_LEVELS[level]) {
      console.warn('[useDefectsConfig] Invalid risk level:', level);
      return false;
    }
    return updateSetting('defaultRiskLevel', level);
  }, [updateSetting]);

  /**
   * Update resolution target days
   */
  const setResolutionTargetDays = useCallback(async (days) => {
    const clamped = Math.min(365, Math.max(1, days));
    return updateSetting('resolutionTargetDays', clamped);
  }, [updateSetting]);

  /**
   * Toggle require corrective action on close
   */
  const setRequireCorrectiveActionOnClose = useCallback(async (require) => {
    return updateSetting('requireCorrectiveActionOnClose', !!require);
  }, [updateSetting]);

  /**
   * Toggle show resolved defects
   */
  const setShowResolvedDefects = useCallback(async (show) => {
    return updateSetting('showResolvedDefects', !!show);
  }, [updateSetting]);

  /**
   * Reset to defaults
   */
  const resetToDefaults = useCallback(async () => {
    if (!canDo(can, 'settings', 'update')) {
      console.warn('[useDefectsConfig] Permission denied');
      return false;
    }

    const defaults = getDefaultConfig();
    defaults.revision = (config.revision || 0) + 1;
    defaults.lastModified = new Date().toISOString();
    defaults.modifiedBy = { name: currentUser?.name, userId: currentUser?.id };

    await setConfig(defaults);

    // Log to audit trail
    // Phase 2.6: Audit temporarily disabled for IndexedDB migration
    // try {
    //   await logCRUDOperation(
    //     collections,
    //     AUDIT_ACTIONS.UPDATE,
    //     'defects_config',
    //     { vesselId: currentVessel?.id, action: 'reset_to_defaults' },
    //     { userId: currentUser?.id, name: currentUser?.name }
    //   );
    // } catch (e) {
    //   console.warn('[useDefectsConfig] Audit log failed:', e);
    // }

    return true;
  }, [can, config, setConfig, currentUser, currentVessel]);

  // ============================================================================
  // RETURN API
  // ============================================================================

  return {
    // Raw config
    config,

    // Computed values
    currentDefaultRiskLevel,
    resolutionTarget,
    defaultRiskLevel: config.defaultRiskLevel,
    resolutionTargetDays: config.resolutionTargetDays,
    requireCorrectiveActionOnClose: config.requireCorrectiveActionOnClose,
    showResolvedDefects: config.showResolvedDefects,

    // Individual mutations
    setDefaultRiskLevel,
    setResolutionTargetDays,
    setRequireCorrectiveActionOnClose,
    setShowResolvedDefects,
    resetToDefaults,

    // Generic update
    updateSetting,

    // Metadata
    lastModified: config.lastModified,
    modifiedBy: config.modifiedBy,
    revision: config.revision,

    // Constants for UI
    RISK_LEVELS
  };
};

export default useDefectsConfig;
